import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState, type ComponentProps, type KeyboardEvent } from "react";
import { cn } from "../lib/cn";
import {
  addDays,
  addMonths,
  monthGrid,
  parseIsoDate,
  sameMonth,
  toIsoDate,
  weekday,
  type CalendarDate,
} from "../lib/date";
import { IconButton } from "./IconButton";

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface CalendarProps extends Omit<ComponentProps<"div">, "onChange"> {
  /** Selected date as `YYYY-MM-DD`. */
  value?: string;
  onChange?: (date: string) => void;
  /** Today as `YYYY-MM-DD`; passed in so rendering is deterministic. */
  today: string;
  /** 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: Weekday;
  /** BCP 47 locale for month and weekday names. Defaults to the runtime locale. */
  locale?: string;
}

// A known Sunday, used to derive localized weekday names.
const REFERENCE_SUNDAY: CalendarDate = { year: 2023, month: 1, day: 1 };

function toDate(d: CalendarDate): Date {
  return new Date(Date.UTC(d.year, d.month - 1, d.day));
}

function parseOrThrow(value: string, name: string): CalendarDate {
  const parsed = parseIsoDate(value);
  if (parsed === null) throw new Error(`Calendar: ${name} must be YYYY-MM-DD, got "${value}"`);
  return parsed;
}

/**
 * A month grid date picker. Arrow keys move by day and week, PageUp/PageDown by
 * month (with Shift: by year), Home/End to the start/end of the week, Enter or
 * Space selects. Only one day is in the tab order at a time.
 */
export function Calendar({
  value,
  onChange,
  today,
  weekStartsOn = 0,
  locale,
  className,
  ...props
}: CalendarProps) {
  const todayDate = parseOrThrow(today, "today");
  const selected = value === undefined ? null : parseOrThrow(value, "value");
  const initial = selected ?? todayDate;

  const [view, setView] = useState<CalendarDate>({ ...initial, day: 1 });
  const [focused, setFocused] = useState<CalendarDate>(initial);
  const [focusRequest, setFocusRequest] = useState(0);
  const [prevValue, setPrevValue] = useState(value);
  const gridRef = useRef<HTMLTableSectionElement>(null);

  // Follow the selected value when it changes from outside (React's "adjust state on prop change").
  if (value !== prevValue) {
    setPrevValue(value);
    if (selected !== null) {
      setView({ ...selected, day: 1 });
      setFocused(selected);
    }
  }

  useEffect(() => {
    if (focusRequest === 0) return;
    const button = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${toIsoDate(focused)}"]`,
    );
    button?.focus();
  }, [focusRequest, focused]);

  const monthFormat = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  const weekdayLongFormat = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" });
  const dayFormat = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

  const weekdays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(REFERENCE_SUNDAY, (weekStartsOn + i) % 7);
    return { short: weekdayFormat.format(toDate(d)), long: weekdayLongFormat.format(toDate(d)) };
  });

  const cells = monthGrid(view.year, view.month, weekStartsOn);
  const weeks: CalendarDate[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const focusDate = (next: CalendarDate): void => {
    setFocused(next);
    if (!sameMonth(next, view)) setView({ ...next, day: 1 });
    setFocusRequest((n) => n + 1);
  };

  const shiftMonth = (delta: number): void => {
    const nextView = addMonths(view, delta);
    setView({ ...nextView, day: 1 });
    setFocused((f) => addMonths(f, delta));
  };

  const select = (date: CalendarDate): void => {
    onChange?.(toIsoDate(date));
    setFocused(date);
    if (!sameMonth(date, view)) setView({ ...date, day: 1 });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>): void => {
    let next: CalendarDate | null = null;
    switch (event.key) {
      case "ArrowLeft":
        next = addDays(focused, -1);
        break;
      case "ArrowRight":
        next = addDays(focused, 1);
        break;
      case "ArrowUp":
        next = addDays(focused, -7);
        break;
      case "ArrowDown":
        next = addDays(focused, 7);
        break;
      case "Home":
        next = addDays(focused, -((weekday(focused) - weekStartsOn + 7) % 7));
        break;
      case "End":
        next = addDays(focused, 6 - ((weekday(focused) - weekStartsOn + 7) % 7));
        break;
      case "PageUp":
        next = addMonths(focused, event.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        next = addMonths(focused, event.shiftKey ? 12 : 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        select(focused);
        return;
      default:
        return;
    }
    event.preventDefault();
    focusDate(next);
  };

  const title = monthFormat.format(toDate(view));

  return (
    <div
      data-slot="calendar"
      className={cn("inline-flex w-fit flex-col gap-2 p-3", className)}
      {...props}
    >
      <div data-slot="calendar-header" className="flex items-center justify-between gap-2">
        <IconButton label="Previous month" size="sm" onClick={() => shiftMonth(-1)}>
          <ChevronLeft />
        </IconButton>
        <div data-slot="calendar-title" aria-live="polite" className="text-sm font-medium text-fg">
          {title}
        </div>
        <IconButton label="Next month" size="sm" onClick={() => shiftMonth(1)}>
          <ChevronRight />
        </IconButton>
      </div>
      <table data-slot="calendar-grid" role="grid" aria-label={title} className="border-collapse">
        <thead>
          <tr>
            {weekdays.map((day) => (
              <th
                key={day.long}
                scope="col"
                abbr={day.long}
                className="size-8 text-center text-xs font-normal text-fg-faint"
              >
                {day.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={gridRef} onKeyDown={handleKeyDown}>
          {weeks.map((week) => (
            <tr key={toIsoDate(week[0] ?? view)}>
              {week.map((date) => {
                const iso = toIsoDate(date);
                const isSelected = selected !== null && iso === toIsoDate(selected);
                const isToday = iso === today;
                const isFocused = iso === toIsoDate(focused);
                const outside = !sameMonth(date, view);
                return (
                  <td key={iso} role="gridcell" aria-selected={isSelected} className="p-0">
                    <button
                      type="button"
                      data-slot="calendar-day"
                      data-date={iso}
                      data-selected={isSelected ? "" : undefined}
                      data-today={isToday ? "" : undefined}
                      data-outside={outside ? "" : undefined}
                      tabIndex={isFocused ? 0 : -1}
                      aria-label={dayFormat.format(toDate(date))}
                      aria-current={isToday ? "date" : undefined}
                      onClick={() => select(date)}
                      className="size-8 rounded-md text-sm text-fg transition-colors duration-fast outline-none select-none hover:bg-fg/6 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus data-outside:text-fg-faint data-today:font-semibold data-today:text-accent data-selected:bg-accent data-selected:text-accent-fg data-selected:hover:bg-accent/90 data-selected:data-today:text-accent-fg"
                    >
                      {date.day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
